import {
  faro,
  initializeFaro,
  getWebInstrumentations,
} from "@grafana/faro-web-sdk";
import { OtlpHttpTransport } from "@grafana/faro-transport-otlp-http";
import * as Sentry from "@sentry/react";

const OTEL_BASE = import.meta.env.VITE_OTEL_COLLECTOR_URL;

export function initFaro() {
  if (!OTEL_BASE) {
    console.warn(
      "[Faro] VITE_OTEL_COLLECTOR_URL not set, skipping initialization"
    );
    return;
  }

  initializeFaro({
    transports: [
      new OtlpHttpTransport({
        logsURL: `${OTEL_BASE}/v1/logs`,
        tracesURL: `${OTEL_BASE}/v1/traces`,
      }),
    ],
    app: {
      name: "altair-web",
      version: import.meta.env.VITE_APP_VERSION ?? "0.0.0",
      environment:
        import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    },
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: false,
      }),
      // TracingInstrumentation removed — Sentry owns distributed tracing.
      // Faro's TracingInstrumentation generates its own trace IDs via
      // traceparent headers, conflicting with Sentry's sentry-trace headers.
      // Faro still captures RUM telemetry (page loads, web vitals, logs).
    ],
  });

  // Link Faro ↔ Sentry for cross-tool correlation
  try {
    // Tag Sentry with the Faro session ID
    const session = faro.api.getSession();
    if (session?.id) {
      Sentry.setTag("faro.session_id", session.id);
    }

    // Tag Faro with the Sentry trace ID so every Faro event carries it.
    // This lets you join Faro RUM telemetry with Sentry distributed traces.
    const sentryTraceId = Sentry.getActiveSpan()?.spanContext().traceId;
    if (sentryTraceId) {
      faro.api.setView({ name: sentryTraceId }); // view name = trace_id for correlation
    }
  } catch {
    // Faro not ready yet — non-fatal
  }
}

/**
 * Push a Faro event enriched with the current Sentry trace ID.
 * Use this instead of raw faro.api.pushEvent() to ensure every
 * custom event can be correlated with the distributed trace.
 */
export function pushFaroEvent(
  name: string,
  attributes: Record<string, string> = {}
): void {
  try {
    const traceId = Sentry.getActiveSpan()?.spanContext().traceId;
    faro.api.pushEvent(name, {
      ...attributes,
      ...(traceId ? { sentry_trace_id: traceId } : {}),
    });
  } catch {
    // Faro not initialized — non-fatal
  }
}

/**
 * Set user + org context on Faro after login.
 * This appears in App Insights spans/traces as user attributes.
 */
export function setFaroUser(userId: string, orgId?: string): void {
  try {
    faro.api.setUser({
      id: userId,
      attributes: {
        ...(orgId ? { org_id: orgId } : {}),
      },
    });
  } catch {
    // Faro not initialized — non-fatal
  }
}

/**
 * Clear user context on logout.
 */
export function clearFaroUser(): void {
  try {
    faro.api.resetUser();
  } catch {
    // Faro not initialized — non-fatal
  }
}

export function getFaroSessionId(): string | undefined {
  try {
    return faro?.api?.getSession()?.id;
  } catch {
    return undefined;
  }
}
